<template>
  <div class="bg-white rounded-xl shadow-sm min-h-[calc(100vh-2rem)] flex flex-col">
    <!-- Header / Stepper -->
    <div class="p-6 border-b border-gray-100">
      <h2 class="text-xl font-bold text-gray-800 mb-6">专科问诊</h2>
      
      <!-- Simple Stepper -->
      <div class="flex items-center justify-between max-w-3xl mx-auto">
        <div v-for="(step, index) in steps" :key="index" class="flex flex-col items-center relative z-10">
          <div 
            class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300"
            :class="[
              currentStep > index + 1 ? 'bg-green-500 text-white' : 
              currentStep === index + 1 ? 'bg-primary-green text-white ring-4 ring-green-100' : 'bg-gray-200 text-gray-500'
            ]"
          >
            <span v-if="currentStep > index + 1">✓</span>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span class="mt-2 text-xs font-medium" :class="currentStep >= index + 1 ? 'text-gray-800' : 'text-gray-400'">
            {{ step }}
          </span>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 p-6 max-w-7xl mx-auto w-full">
      
      <!-- Step 1: Select Patient -->
      <div v-if="currentStep === 1" class="space-y-6">
        <div class="flex justify-between items-center">
          <h3 class="text-lg font-semibold text-gray-700">选择用户</h3>
          <button 
            @click="showCreatePatientModal = true"
            class="bg-primary-green hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm transition shadow-sm flex items-center"
          >
            <span class="mr-1 text-lg">+</span> 新增用户
          </button>
        </div>

        <div class="relative">
          <input 
            v-model="searchQuery"
            @input="handleSearch"
            @keyup.enter="handleSearchImmediate"
            type="text" 
            placeholder="输入姓名或手机号搜索用户..." 
            class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition"
          >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <div v-if="loadingPatients" class="text-center py-10 text-gray-500">
          加载中...
        </div>

        <div v-else-if="patients.length === 0" class="text-center py-10 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
          未找到相关用户，请尝试搜索其他关键词或新增用户
        </div>

        <div v-else>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div 
              v-for="patient in patients" 
              :key="patient.id"
              @click="selectPatient(patient)"
              class="p-4 border rounded-xl cursor-pointer hover:border-green-500 hover:shadow-md transition group bg-white"
              :class="selectedPatient?.id === patient.id ? 'border-green-500 bg-green-50' : 'border-gray-100'"
            >
              <div class="flex justify-between items-start">
                <div>
                  <div class="flex items-center space-x-2">
                    <span class="font-bold text-gray-800">{{ patient.name }}</span>
                    <span class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{{ patient.gender }}</span>
                    <span class="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{{ patient.age }}岁</span>
                  </div>
                  <p class="text-sm text-gray-500 mt-2 line-clamp-1">{{ patient.remark || '无备注' }}</p>
                </div>
                <div class="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center group-hover:border-green-500">
                   <div v-if="selectedPatient?.id === patient.id" class="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-center mt-6">
            <button
              class="px-4 py-2 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm text-gray-600"
              :disabled="loadingPatients || !patientsHasMore"
              @click="loadMorePatients"
            >
              <span v-if="loadingPatients">加载中...</span>
              <span v-else-if="patientsHasMore">加载更多</span>
              <span v-else>没有更多了</span>
            </button>
          </div>
        </div>

        <div class="flex justify-end mt-8">
          <button 
            @click="nextStep" 
            :disabled="!selectedPatient"
            class="bg-gray-900 text-white px-8 py-2.5 rounded-lg hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            下一步
          </button>
        </div>
      </div>

      <!-- Step 2: Select Clinic -->
      <div v-if="currentStep === 2" class="grid grid-cols-12 gap-6 h-full items-start">
        <!-- Left: Patient Info -->
        <div class="col-span-3">
          <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm sticky top-4">
            <h4 class="font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3 flex items-center">
              <span class="w-1 h-4 bg-primary-green rounded mr-2"></span>
              用户信息
            </h4>
            <div class="space-y-4">
              <div>
                <span class="text-xs text-gray-500 block mb-1">姓名</span>
                <span class="font-bold text-gray-800 text-lg">{{ selectedPatient?.name }}</span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <span class="text-xs text-gray-500 block mb-1">性别</span>
                  <span class="font-medium text-gray-800 bg-gray-50 px-2 py-1 rounded inline-block">{{ selectedPatient?.gender }}</span>
                </div>
                <div>
                  <span class="text-xs text-gray-500 block mb-1">年龄</span>
                  <span class="font-medium text-gray-800 bg-gray-50 px-2 py-1 rounded inline-block">{{ selectedPatient?.age }}岁</span>
                </div>
              </div>
              <div>
                <span class="text-xs text-gray-500 block mb-1">备注</span>
                <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg leading-relaxed">{{ selectedPatient?.remark || '暂无备注信息' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle: Clinic Selection -->
        <div class="col-span-6 space-y-6">
          <div class="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h3 class="text-lg font-semibold text-gray-700">选择专科门诊</h3>
          </div>
  
          <div v-if="loadingClinics" class="text-center py-20 text-gray-500 bg-white rounded-xl border border-dashed border-gray-200">
            加载门诊列表中...
          </div>
  
          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div 
              v-for="clinic in clinics" 
              :key="clinic.id"
              @click="selectClinic(clinic)"
              class="p-5 border rounded-xl cursor-pointer hover:border-green-500 hover:shadow-md transition relative overflow-hidden bg-white group"
              :class="selectedClinic?.id === clinic.id ? 'border-green-500 bg-green-50 ring-1 ring-green-500' : 'border-gray-100'"
            >
              <h4 class="font-bold text-gray-800 mb-2 group-hover:text-primary-green transition-colors">{{ clinic.name }}</h4>
              <p class="text-sm text-gray-500 leading-relaxed">{{ clinic.description }}</p>
              <div class="absolute top-0 right-0 p-2" v-if="selectedClinic?.id === clinic.id">
                <svg class="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
              </div>
            </div>
          </div>
  
          <div class="flex justify-between mt-8 pt-4 border-t border-gray-100">
            <button @click="prevStep" class="text-gray-500 hover:text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-100 transition">上一步</button>
            <button 
              @click="startDiagnosisProcess" 
              :disabled="!selectedClinic"
              class="bg-gray-900 text-white px-8 py-2.5 rounded-lg hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-gray-200"
            >
              开始问诊
            </button>
          </div>
        </div>

        <!-- Right: Empty -->
        <div class="col-span-3">
        </div>
      </div>

      <!-- Step 3: Diagnosis Questionnaire -->
      <div v-if="currentStep === 3" class="grid grid-cols-12 gap-6 h-full items-start">
         <!-- Left: Patient Info -->
        <div class="col-span-3">
          <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm sticky top-4">
            <h4 class="font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3 flex items-center">
              <span class="w-1 h-4 bg-primary-green rounded mr-2"></span>
              用户信息
            </h4>
            <div class="space-y-4">
              <div>
                <span class="text-xs text-gray-500 block mb-1">姓名</span>
                <span class="font-bold text-gray-800 text-lg">{{ selectedPatient?.name }}</span>
              </div>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <span class="text-xs text-gray-500 block mb-1">性别</span>
                  <span class="font-medium text-gray-800 bg-gray-50 px-2 py-1 rounded inline-block">{{ selectedPatient?.gender }}</span>
                </div>
                <div>
                  <span class="text-xs text-gray-500 block mb-1">年龄</span>
                  <span class="font-medium text-gray-800 bg-gray-50 px-2 py-1 rounded inline-block">{{ selectedPatient?.age }}岁</span>
                </div>
              </div>
              <div>
                <span class="text-xs text-gray-500 block mb-1">备注</span>
                <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg leading-relaxed">{{ selectedPatient?.remark || '暂无备注信息' }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle: Question -->
        <div class="col-span-6">
             <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-8 min-h-[400px] flex flex-col justify-center">
                 <div v-if="loadingQuestion" class="flex flex-col items-center justify-center py-12">
                   <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-green mb-4"></div>
                   <p class="text-gray-500">正在获取题目...</p>
                 </div>
        
                <div v-else-if="currentQuestion" class="animate-fade-in-up">
                  <div class="mb-8">
                    <span class="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full mb-3">
                      问题 {{ diagnosisHistory.length + 1 }}
                    </span>
                    <h3 class="text-2xl font-bold text-gray-800 leading-relaxed">
                      {{ currentQuestion.text }}
                    </h3>
                  </div>
        
                  <div class="space-y-3">
                    <button 
                      v-for="option in currentQuestion.options" 
                      :key="option.value"
                      @click="submitAnswer(option.value)"
                      class="w-full text-left p-4 border border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-all duration-200 flex justify-between items-center group"
                    >
                      <span class="text-gray-700 font-medium group-hover:text-green-800">{{ option.label }}</span>
                      <span class="w-4 h-4 border border-gray-300 rounded-full group-hover:border-green-500"></span>
                    </button>
                  </div>
                  <div class="mt-6">
                    <button
                      v-if="currentStep === 3 && diagnosisStatus === 'next_question' && diagnosisStepNumber > 1"
                      @click="rollbackToPrevQuestion"
                      class="px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                    >
                      上一题
                    </button>
                  </div>
                </div>
             </div>
        </div>

        <!-- Right: History -->
        <div class="col-span-3">
             <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm sticky top-4 h-auto max-h-[calc(100vh-10rem)] overflow-y-auto">
                <h4 class="font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3 flex items-center justify-between">
                    <div class="flex items-center">
                         <span class="w-1 h-4 bg-gray-800 rounded mr-2"></span>
                         问诊记录
                    </div>
                    <span class="text-xs text-gray-400 font-normal">{{ diagnosisHistory.length }} 条记录</span>
                </h4>
                
                <div v-if="diagnosisHistory.length === 0" class="text-center py-10 text-gray-400 text-sm">
                    暂无问诊记录
                </div>

                <div v-else class="space-y-4">
                    <div v-for="(item, index) in diagnosisHistory" :key="index" class="relative pl-4 border-l-2 border-gray-200 pb-1 last:pb-0">
                        <div class="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-gray-300"></div>
                        <p class="text-xs text-gray-500 mb-1">问题 {{ index + 1 }}</p>
                        <p class="text-sm font-medium text-gray-800 mb-1">{{ item.question }}</p>
                        <p class="text-sm text-primary-green bg-green-50 p-2 rounded inline-block">
                             {{ item.answer }}
                        </p>
                    </div>
                </div>
             </div>
        </div>
      </div>

      <!-- Step 4: Diagnosis Result -->
      <div v-if="currentStep === 4" class="grid grid-cols-12 gap-6 h-full items-start">
        <!-- Left: Patient Info -->
        <div class="col-span-3">
          <div class="sticky top-4 space-y-4">
            <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
              <h4 class="font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3 flex items-center">
                <span class="w-1 h-4 bg-primary-green rounded mr-2"></span>
                用户信息
              </h4>
              <div class="space-y-4">
                <div>
                  <span class="text-xs text-gray-500 block mb-1">姓名</span>
                  <span class="font-bold text-gray-800 text-lg">{{ selectedPatient?.name }}</span>
                </div>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <span class="text-xs text-gray-500 block mb-1">性别</span>
                    <span class="font-medium text-gray-800 bg-gray-50 px-2 py-1 rounded inline-block">{{ selectedPatient?.gender }}</span>
                  </div>
                  <div>
                    <span class="text-xs text-gray-500 block mb-1">年龄</span>
                    <span class="font-medium text-gray-800 bg-gray-50 px-2 py-1 rounded inline-block">{{ selectedPatient?.age }}岁</span>
                  </div>
                </div>
                <div>
                  <span class="text-xs text-gray-500 block mb-1">备注</span>
                  <p class="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg leading-relaxed">{{ selectedPatient?.remark || '暂无备注信息' }}</p>
                </div>
              </div>
            </div>

            <div v-if="aiConsultationCompleted && aiConsultationResult" class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
              <h4 class="font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3 flex items-center">
                <span class="w-1 h-4 bg-primary-green rounded mr-2"></span>
                深度辨证
              </h4>
              <div class="bg-gray-50 p-3 rounded-lg max-h-[40vh] overflow-y-auto">
                <p class="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{{ aiConsultationResult }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Middle: Result -->
        <div class="col-span-6">
           <!-- <div class="bg-green-50 rounded-2xl p-8 border border-green-100 text-center mb-6">
             <h3 class="text-2xl font-bold text-gray-800 mb-2">问诊结束</h3>
             <p class="text-gray-600">根据您的回答，系统初步诊断如下：</p>
           </div> -->
           <div class="bg-yellow-50 text-yellow-700 text-sm p-4 rounded-xl border border-yellow-200 mb-6">
             本系统提供的所有辩证和方剂信息仅供专业医师参考，不针对普通用户，不构成医疗建议。诊疗方案必须以专业医师诊断为准，普通用户不可直接使用本系统内容。
           </div>
   
           <div class="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
             <div class="p-6 border-b border-gray-50">
               <h4 class="text-lg font-bold text-gray-800 mb-4">诊断报告</h4>
               
               <div class="space-y-4">
                 <div>
                   <span class="text-sm text-gray-500 block mb-1">初步诊断</span>
                   <p class="text-xl font-medium text-primary-green">{{ diagnosisResult?.disease }}</p>
                 </div>
                 
                 <div>
                   <span class="text-sm text-gray-500 block mb-1">健康建议</span>
                   <p class="text-gray-700 bg-gray-50 p-3 rounded-lg">{{ diagnosisResult?.advice }}</p>
                 </div>

                 <div v-if="diagnosisResult?.corePathogenesis">
                   <span class="text-sm text-gray-500 block mb-1">核心病机</span>
                   <p class="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-line">{{ diagnosisResult.corePathogenesis }}</p>
                 </div>

                 <div v-if="diagnosisResult?.symptomGroups">
                   <span class="text-sm text-gray-500 block mb-1">主要症状群</span>
                   <p class="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-line">{{ diagnosisResult.symptomGroups }}</p>
                 </div>

                 <div v-if="diagnosisResult?.differentialExclusions">
                   <span class="text-sm text-gray-500 block mb-1">鉴别排除</span>
                   <p class="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-line">{{ diagnosisResult.differentialExclusions }}</p>
                 </div>

                 <div v-if="diagnosisResult?.recommendedPrescription">
                   <div class="flex items-center justify-between">
                     <span class="text-sm text-gray-500 block mb-1">建议方剂</span>
                     <button @click="copyText(diagnosisResult.recommendedPrescription)" class="text-xs text-primary-green hover:text-green-600 px-2 py-0.5 rounded hover:bg-green-50 transition">复制</button>
                   </div>
                   <p class="text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-line">{{ diagnosisResult.recommendedPrescription }}</p>
                 </div>

                 <div
                   v-if="diagnosisResult && diagnosisResult.branchMatchings && diagnosisResult.branchMatchings.length"
                 >
                   <span class="text-sm text-gray-500 block mb-1">证型匹配列表</span>
                   <div class="space-y-2">
                     <div
                      v-for="(item, index) in diagnosisResult.branchMatchings"
                      :key="index"
                      @click="openPrescriptionModal(item)"
                      class="flex items-center justify-between bg-gray-50 p-3 rounded-lg hover:bg-green-50 hover:shadow-sm transition cursor-pointer"
                    >
                       <span class="text-sm text-gray-700">{{ item.patternName }}</span>
                       <span class="text-sm text-primary-green font-medium">
                         {{ formatMatchPercentage(item.matchPercentage) }}
                       </span>
                     </div>
                   </div>
                 </div>
   
                 <!-- <div>
                   <span class="text-sm text-gray-500 block mb-1">诊断置信度</span>
                   <div class="flex items-center">
                     <div class="flex-1 bg-gray-100 rounded-full h-2 mr-3">
                       <div class="bg-primary-green h-2 rounded-full" :style="{ width: diagnosisResult?.score + '%' }"></div>
                     </div>
                     <span class="text-sm font-bold text-gray-700">{{ diagnosisResult?.score }}%</span>
                   </div>
                 </div> -->
               </div>
             </div>
             
             <div class="p-6 bg-gray-50 flex justify-center space-x-4">
               <button @click="resetDiagnosis" class="px-6 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-white transition">
                 返回列表
               </button>
               <button 
                 v-if="showAiButton"
                 :disabled="aiConsultationCompleted || aiConsultationLoading"
                 @click="startAiConsultation"
                 class="px-6 py-2 bg-primary-green text-white rounded-lg hover:bg-green-600 transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-primary-green">
                 进一步辩证
               </button>
               <!-- <button class="px-6 py-2 bg-primary-green text-white rounded-lg hover:bg-green-600 transition shadow-sm">
                 生成详细病历
               </button> -->
             </div>
           </div>
        </div>

        <!-- Right: History -->
        <div class="col-span-3">
             <div class="bg-white p-5 rounded-xl border border-gray-100 shadow-sm sticky top-4 h-auto max-h-[calc(100vh-10rem)] overflow-y-auto">
                <h4 class="font-bold text-gray-800 mb-4 border-b border-gray-100 pb-3 flex items-center justify-between">
                    <div class="flex items-center">
                         <span class="w-1 h-4 bg-gray-800 rounded mr-2"></span>
                         问诊记录
                    </div>
                    <span class="text-xs text-gray-400 font-normal">{{ diagnosisHistory.length }} 条记录</span>
                </h4>
                
                <div v-if="diagnosisHistory.length === 0" class="text-center py-10 text-gray-400 text-sm">
                    暂无问诊记录
                </div>

                <div v-else class="space-y-4">
                    <div v-for="(item, index) in diagnosisHistory" :key="index" class="relative pl-4 border-l-2 border-gray-200 pb-1 last:pb-0">
                        <div class="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-gray-300"></div>
                        <p class="text-xs text-gray-500 mb-1">问题 {{ index + 1 }}</p>
                        <p class="text-sm font-medium text-gray-800 mb-1">{{ item.question }}</p>
                        <p class="text-sm text-primary-green bg-green-50 p-2 rounded inline-block">
                             {{ item.answer }}
                        </p>
                    </div>
                </div>
             </div>
        </div>
      </div>
    </div>

    <!-- Prescription Modal -->
    <div v-if="showPrescriptionModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm" @click.self="closePrescriptionModal">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 transform transition-all scale-100 relative">
        <button @click="closePrescriptionModal" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        
        <h3 class="text-xl font-bold text-gray-800 mb-2">方剂详情</h3>
        <p class="text-sm text-gray-500 mb-6" v-if="currentPrescription">
          针对证型：<span class="font-medium text-gray-700">{{ currentPrescription.patternName }}</span>
        </p>
        
        <div class="bg-gray-50 p-4 rounded-xl max-h-[60vh] overflow-y-auto relative group">
          <button 
            v-if="currentPrescription?.prescription"
            @click="copyText(currentPrescription.prescription)"
            class="absolute top-2 right-2 text-xs bg-white border border-gray-200 text-gray-600 px-2 py-1 rounded hover:text-primary-green hover:border-green-500 transition shadow-sm opacity-0 group-hover:opacity-100"
          >
            复制
          </button>
          <p class="text-gray-700 whitespace-pre-line leading-relaxed">{{ currentPrescription?.prescription || '暂无方剂信息' }}</p>
        </div>
        
        <div class="mt-6 flex justify-end">
          <button @click="closePrescriptionModal" class="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">关闭</button>
        </div>
      </div>
    </div>

    <!-- Create Patient Modal -->
    <div v-if="showCreatePatientModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 transform transition-all scale-100">
        <h3 class="text-xl font-bold text-gray-800 mb-6">新增用户</h3>
        
        <form @submit.prevent="handleCreatePatient" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">姓名 <span class="text-red-500">*</span></label>
            <input v-model="newPatient.name" type="text" required class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500">
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">手机号</label>
              <input v-model="newPatient.mobile" type="tel" class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">出生日期 <span class="text-red-500">*</span></label>
              <input v-model="newPatient.birthday" type="date" required class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500">
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">性别 <span class="text-red-500">*</span></label>
              <select v-model="newPatient.gender" required class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500 bg-white">
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">备注</label>
              <textarea v-model="newPatient.remark" rows="3" class="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green-500"></textarea>
            </div>
          </div>

          <div class="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-100">
            <button type="button" @click="showCreatePatientModal = false" class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition">取消</button>
            <button type="submit" :disabled="creatingPatient" class="px-6 py-2 bg-primary-green text-white rounded-lg hover:bg-green-600 transition shadow-sm disabled:opacity-70">
              {{ creatingPatient ? '保存中...' : '保存' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- AI Consultation Modal -->
    <div v-if="showAiConsultationModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm" @click.self="closeAiConsultationModal">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 transform transition-all scale-100 relative max-h-[80vh] flex flex-col">
        <button @click="closeAiConsultationModal" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
        </button>
        
        <h3 class="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <span class="w-1 h-4 bg-primary-green rounded mr-2"></span>
            深度辨证分析
        </h3>
        
        <div class="bg-gray-50 p-6 rounded-xl flex-1 overflow-y-auto min-h-[300px]">
          <div v-if="!aiConsultationContent && aiConsultationLoading" class="flex justify-center items-center h-full text-gray-400">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-green mr-2"></div>
              正在分析中...
          </div>
          <div v-else class="prose max-w-none text-gray-700 whitespace-pre-line leading-relaxed relative group">
             <button 
                v-if="aiConsultationContent && !aiConsultationLoading"
                @click="copyText(aiConsultationContent)"
                class="absolute top-0 right-0 text-xs bg-white border border-gray-200 text-gray-600 px-2 py-1 rounded hover:text-primary-green hover:border-green-500 transition shadow-sm opacity-0 group-hover:opacity-100"
              >
                复制
             </button>
             {{ aiConsultationContent }}
             <span v-if="aiConsultationLoading" class="inline-block w-1.5 h-4 bg-primary-green animate-pulse ml-1 align-middle"></span>
          </div>
        </div>
        
        <div class="mt-6 flex justify-end">
          <button @click="closeAiConsultationModal" class="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest, parsePagination } from '@/utils/request';

export default {
  name: 'SpecializedDiagnosis',
  data() {
    return {
      currentStep: 1,
      steps: ['选择用户', '选择专科', '智能问诊', '诊断结果'],
      
      // Step 1: Patient
      patients: [],
      searchQuery: '',
      selectedPatient: null,
      loadingPatients: false,
      patientsPage: 1,
      patientsHasMore: false,
      searchTimeout: null,
      showCreatePatientModal: false,
      newPatient: {
        name: '',
        mobile: '',
        birthday: '',
        gender: '男',
        remark: ''
      },
      creatingPatient: false,

      // Step 2: Clinic
      clinics: [],
      selectedClinic: null,
      loadingClinics: false,

      // Step 3: Diagnosis
      sessionId: null,
      currentQuestion: null,
      loadingQuestion: false,
      diagnosisHistory: [], // Added for history tracking
      diagnosisStatus: null,
      diagnosisStepNumber: 1,

      // Step 4: Result
      diagnosisResult: null,
      
      // Prescription Modal
      showPrescriptionModal: false,
      currentPrescription: null,

      // AI Consultation
      showAiButton: false,
      showAiConsultationModal: false,
      aiConsultationContent: '',
      aiConsultationResult: '',
      aiConsultationLoading: false,
      aiConsultationAbortController: null,
      aiConsultationCancelled: false,
      aiConsultationCompleted: false
    };
  },
  mounted() {
    this.fetchPatients(1);
  },
  methods: {
    // --- Step 1: Patient Logic ---
    async fetchPatients(targetPage = 1) {
      if (this.loadingPatients) return;
      this.loadingPatients = true;
      try {
        const response = await httpRequest('get', '/v1/patients', {
          headers: {
            'Client-Type': '1'
          },
          query: {
            page: targetPage,
            name: this.searchQuery || ''
          }
        });
        const pagination = parsePagination(response, targetPage, 'data');
        const patientList = Array.isArray(pagination.list) ? pagination.list : [];
        const mappedPatients = patientList.map((item) => ({
          id: item.id,
          name: item.name,
          gender: item.gender,
          age: item.age,
          remark: item.remark || ''
        }));
        if (targetPage === 1) {
          this.patients = mappedPatients;
        } else {
          this.patients = this.patients.concat(mappedPatients);
        }
        this.patientsPage = pagination.page || targetPage;
        this.patientsHasMore = !!pagination.has_more;
      } catch (error) {
        console.error(error);
        this.patientsHasMore = false;
      } finally {
        this.loadingPatients = false;
      }
    },
    handleSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.patientsPage = 1;
        this.patientsHasMore = false;
        this.patients = [];
        this.fetchPatients(1);
      }, 300);
    },
    handleSearchImmediate() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.patientsPage = 1;
      this.patientsHasMore = false;
      this.patients = [];
      this.fetchPatients(1);
    },
    loadMorePatients() {
      if (this.loadingPatients || !this.patientsHasMore) return;
      const nextPage = (this.patientsPage || 1) + 1;
      this.fetchPatients(nextPage);
    },
    selectPatient(patient) {
      this.selectedPatient = patient;
    },
    async handleCreatePatient() {
      this.creatingPatient = true;
      try {
        const genderValue = this.newPatient.gender === '女' ? 2 : 1;
        const requestBody = {
          name: this.newPatient.name,
          mobile: this.newPatient.mobile || '',
          birthday: this.newPatient.birthday,
          gender: genderValue,
          remark: this.newPatient.remark || ''
        };
        const response = await httpRequest('post', '/v1/patients', {
          headers: {
            'Client-Type': '1'
          },
          body: requestBody
        });
        const patientData = response && response.data ? response.data : null;
        if (patientData) {
          const created = {
            id: patientData.id,
            name: patientData.name,
            gender: patientData.gender,
            age: patientData.age,
            remark: patientData.remark || ''
          };
          this.selectedPatient = created;
        }
        this.showCreatePatientModal = false;
        this.newPatient = {
          name: '',
          mobile: '',
          birthday: '',
          gender: '男',
          remark: ''
        };
        this.patientsPage = 1;
        this.patientsHasMore = false;
        this.patients = [];
        this.fetchPatients(1);
      } catch (error) {
        alert('创建失败，请重试');
      } finally {
        this.creatingPatient = false;
      }
    },

    // --- Navigation ---
    async nextStep() {
      if (this.currentStep === 1) {
        if (!this.selectedPatient) return;
        this.currentStep = 2;
        this.fetchClinics();
      } else if (this.currentStep === 2) {
        // Handled by startDiagnosisProcess
      }
    },
    prevStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    // --- Step 2: Clinic Logic ---
    async fetchClinics() {
      this.loadingClinics = true;
      try {
        const response = await httpRequest('get', '/v1/diseases', {
          headers: {
            'Client-Type': '1'
          }
        });
        const payload_data = response && response.data ? response.data : response;
        const clinic_list = payload_data && Array.isArray(payload_data.data) ? payload_data.data : [];
        this.clinics = clinic_list.map((item) => ({
          id: item.code,
          code: item.code,
          name: item.name,
          description: item.description || ''
        }));
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingClinics = false;
      }
    },
    selectClinic(clinic) {
      this.selectedClinic = clinic;
    },
    async startDiagnosisProcess() {
      if (!this.selectedClinic || !this.selectedPatient) return;
      this.currentStep = 3;
      this.loadingQuestion = true;
      this.diagnosisHistory = []; // Reset history
      this.diagnosisStatus = null;
      this.diagnosisStepNumber = 1;
      try {
        const request_body = {
          disease_code: this.selectedClinic.code || this.selectedClinic.id,
          patient_id: this.selectedPatient.id
        };
        const response = await httpRequest('post', '/v1/diagnosis/special/start', {
          headers: {
            'Client-Type': '1'
          },
          body: request_body
        });
        const payload_data = response && response.data ? response.data : response;
        this.sessionId = payload_data.session_id || null;
        const question_data = payload_data.current_question || null;
        this.currentQuestion = this.mapApiQuestion(question_data);
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingQuestion = false;
      }
    },

    // --- Step 3: Diagnosis Logic ---
    mapApiQuestion(question_data) {
      if (!question_data) return null;
      const option_list = Array.isArray(question_data.options) ? question_data.options : [];
      return {
        id: question_data.question_code,
        text: question_data.question_text,
        options: option_list.map((opt) => ({
          value: opt.option_code,
          label: opt.option_text,
          hasDiagnosis: typeof opt.has_diagnosis === 'boolean'
            ? opt.has_diagnosis
            : opt.has_diagnosis === 'true'
        }))
      };
    },
    mapDiagnosisResult(result_data) {
      if (!result_data) return null;
      const level = result_data.confidence_level || '';
      let score = 0;
      if (level === 'high') {
        score = 90;
      } else if (level === 'medium') {
        score = 70;
      } else if (level === 'low') {
        score = 40;
      }
      let symptomGroups = result_data.symptom_groups;
      if (Array.isArray(symptomGroups)) {
        symptomGroups = symptomGroups.join('；');
      }
      let differentialExclusions = result_data.differential_exclusions;
      if (Array.isArray(differentialExclusions)) {
        differentialExclusions = differentialExclusions.join('；');
      }
      let branchMatchings = [];
      if (Array.isArray(result_data.branch_matchings)) {
        branchMatchings = result_data.branch_matchings.map((item) => {
          let matchPercentage = item.match_percentage;
          if (typeof matchPercentage === 'number') {
            matchPercentage = String(matchPercentage);
          }
          return {
            patternName: item.pattern_name || '',
            matchPercentage,
          prescription: item.prescription || ''
        };
      });
      }
      return {
        disease: result_data.diagnosis_name || '',
        advice: result_data.consultation_summary || result_data.core_pathogenesis || '',
        corePathogenesis: result_data.core_pathogenesis || '',
        symptomGroups: symptomGroups || '',
        differentialExclusions: differentialExclusions || '',
        recommendedPrescription: result_data.recommended_prescription || '',
        branchMatchings,
        score
      };
    },
    async submitAnswer(answerValue) {
      // Record history before moving on
      const selected_option = this.currentQuestion.options.find((opt) => opt.value === answerValue);
      if (selected_option) {
        this.diagnosisHistory.push({
          question: this.currentQuestion.text,
          answer: selected_option.label
        });
      }

      this.loadingQuestion = true;
      try {
        const request_body = {
          question_code: this.currentQuestion.id,
          selected_options: [answerValue]
        };
        const response = await httpRequest('post', `/v1/diagnosis/special/session/${this.sessionId}/answer`, {
          headers: {
            'Client-Type': '1'
          },
          body: request_body
        });
        const payload_data = response && response.data ? response.data : response;
        const status_value = payload_data.status;
        const step_number_value = typeof payload_data.step_number === 'number' ? payload_data.step_number : null;
        this.diagnosisStatus = status_value || null;
        if (step_number_value !== null) {
          this.diagnosisStepNumber = step_number_value;
        } else if (status_value === 'next_question') {
          this.diagnosisStepNumber = this.diagnosisStepNumber + 1;
        }
        if (status_value === 'completed') {
          const result_data = payload_data.diagnosis_result || null;
          this.diagnosisResult = this.mapDiagnosisResult(result_data);
          this.currentStep = 4;
          if (payload_data.confirmed === false) {
            this.showAiButton = true;
          }
        } else if (status_value === 'next_question') {
          const next_question = payload_data.next_question || null;
          this.currentQuestion = this.mapApiQuestion(next_question);
          if (payload_data.session_id) {
            this.sessionId = payload_data.session_id;
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingQuestion = false;
      }
    },
    async rollbackToPrevQuestion() {
      if (!this.sessionId) return;
      if (this.diagnosisHistory.length > 0) {
        this.diagnosisHistory.pop();
      }
      this.loadingQuestion = true;
      try {
        const response = await httpRequest('post', `/v1/diagnosis/special/session/${this.sessionId}/rollback`, {
          headers: {
            'Client-Type': '1'
          }
        });
        const payload_data = response && response.data ? response.data : response;
        const status_value = payload_data.status;
        const step_number_value = typeof payload_data.step_number === 'number' ? payload_data.step_number : null;
        this.diagnosisStatus = status_value || null;
        if (step_number_value !== null) {
          this.diagnosisStepNumber = step_number_value;
        } else if (this.diagnosisStepNumber > 1) {
          this.diagnosisStepNumber = this.diagnosisStepNumber - 1;
        }
        const next_question = payload_data.next_question || null;
        this.currentQuestion = this.mapApiQuestion(next_question);
        if (payload_data.session_id) {
          this.sessionId = payload_data.session_id;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingQuestion = false;
      }
    },

    // --- Step 4: Result Logic ---
    formatMatchPercentage(value) {
      if (value === null || value === undefined) {
        return '';
      }
      if (typeof value === 'number') {
        return `${value}%`;
      }
      const str = String(value);
      if (str.indexOf('%') !== -1) {
        return str;
      }
      return `${str}%`;
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(err => {
          console.error('Failed to copy: ', err)
        })
      } else {
        const textArea = document.createElement("textarea")
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        try {
          document.execCommand('copy')
        } catch (err) {
          console.error('Fallback: Oops, unable to copy', err)
        }
        document.body.removeChild(textArea)
      }
    },
    resetDiagnosis() {
      // Reset state to initial or records page
      this.currentStep = 1;
      this.selectedPatient = null;
      this.selectedClinic = null;
      this.currentQuestion = null;
      this.diagnosisResult = null;
      this.diagnosisHistory = [];
      this.diagnosisStatus = null;
      this.diagnosisStepNumber = 1;
      this.sessionId = null;
      this.showAiButton = false;
      this.showAiConsultationModal = false;
      this.aiConsultationContent = '';
      this.aiConsultationResult = '';
      this.aiConsultationLoading = false;
      this.aiConsultationAbortController = null;
      this.aiConsultationCancelled = false;
      this.aiConsultationCompleted = false;
      this.fetchPatients();
    },
    openPrescriptionModal(item) {
      if (item) {
        this.currentPrescription = item;
        this.showPrescriptionModal = true;
      }
    },
    closePrescriptionModal() {
      this.showPrescriptionModal = false;
      this.currentPrescription = null;
    },
    // --- AI Consultation ---
    async startAiConsultation() {
      if (this.aiConsultationLoading || this.aiConsultationCompleted) return;
      this.showAiConsultationModal = true;
      this.aiConsultationContent = '';
      this.aiConsultationResult = '';
      this.aiConsultationLoading = true;
      this.aiConsultationCompleted = false;
      
      try {
        const abort_controller = new AbortController();
        this.aiConsultationAbortController = abort_controller;
        this.aiConsultationCancelled = false;

        const response = await httpRequest('get', `/v1/diagnosis/special/session/${this.sessionId}/ai`, {
          headers: {
            'Client-Type': '1',
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
          },
          query: {
            stream: '1'
          },
          stream: true,
          timeout: 0,
          signal: abort_controller.signal
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        if (!response.body) {
             throw new Error('Response body is null');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();

        let ui_buffer = '';
        let last_flush_ms = 0;
        const flush_interval_ms = 50;
        const flushToUi = async () => {
          if (!ui_buffer) return;
          this.aiConsultationContent += ui_buffer;
          ui_buffer = '';
          await new Promise((resolve) => requestAnimationFrame(resolve));
        };

        let pending_text = '';
        let done = false;
        while (!done) {
          const { done: streamDone, value } = await reader.read();
          done = streamDone;
          if (value) {
            pending_text += decoder.decode(value, { stream: true });
            pending_text = pending_text.replace(/\r\n/g, '\n');

            let event_end_index = pending_text.indexOf('\n\n');
            while (event_end_index !== -1) {
              const raw_event = pending_text.slice(0, event_end_index);
              pending_text = pending_text.slice(event_end_index + 2);

              const lines = raw_event.split('\n');
              const data_lines = lines
                .filter((line) => line.startsWith('data:'))
                .map((line) => line.replace(/^data:\s?/, ''));
              const data_text = data_lines.join('\n').trim();
              if (data_text) {
                if (data_text === '[DONE]') {
                  done = true;
                  break;
                }

                let appended = false;
                let parsed_ok = false;
                try {
                  const parsed = JSON.parse(data_text);
                  parsed_ok = true;
                  if (parsed && typeof parsed.delta === 'string') {
                    ui_buffer += parsed.delta;
                    appended = true;
                  }
                } catch (e) {
                  // ignore
                }

                if (!appended && !parsed_ok) {
                  ui_buffer += data_text;
                }

                const now_ms = Date.now();
                if (now_ms - last_flush_ms >= flush_interval_ms) {
                  last_flush_ms = now_ms;
                  await flushToUi();
                }
              }

              event_end_index = pending_text.indexOf('\n\n');
            }
          }
          if (this.aiConsultationCancelled) {
            done = true;
          }
        }

        pending_text += decoder.decode();
        await flushToUi();

        if (!this.aiConsultationCancelled) {
          this.aiConsultationResult = this.aiConsultationContent;
          this.aiConsultationCompleted = true;
        }
        
      } catch (error) {
        console.error('AI Consultation Error:', error);
        if (!this.aiConsultationCancelled) {
          this.aiConsultationContent += '\n\n(获取结果出错: ' + error.message + ')';
        }
      } finally {
        this.aiConsultationLoading = false;
        this.aiConsultationAbortController = null;
      }
    },
    closeAiConsultationModal() {
        this.showAiConsultationModal = false;
        this.aiConsultationCancelled = true;
        if (this.aiConsultationAbortController) {
          try {
            this.aiConsultationAbortController.abort();
          } catch (e) {
            // ignore
          }
        }
        this.aiConsultationAbortController = null;
        this.aiConsultationLoading = false;
    }
  }
}
</script>

<style scoped>
/* Simple fade animation */
.animate-fade-in-up {
  animation: fadeInUp 0.5s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
